import { useState } from "react";
import { useApi } from "src/api";
import { MultiFileUploader } from "src/components/MultiFileUploader/MultiFileUploader";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Icon, Modal, SubgridTable, Text } from "src/elements";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";
import {
  ImageUploadResult,
  ResponsePostInputsProductImages,
  RoutePostInputsProductImages,
} from "@CommonApi/inputs/product-images";

const MAX_PRODUCT_IMAGE_UPLOADS = 50;

const columns: SubgridColumn<ImageUploadResult>[] = [
  {
    id: "productCode",
    header: "Product Code",
    justify: "left",
  },
  {
    id: "status",
    header: "Status",
    justify: "center",
    renderer: ({ value }) => {
      const isSuccess = value === "SUCCESS";
      return (
        <Flex align="center" gap="5px">
          <Icon
            name={isSuccess ? "checkmarkCircle" : "clearCircle"}
            color={isSuccess ? Color.primary : Color.red}
            size={14}
          />
          <Text variant="small2">{value}</Text>
        </Flex>
      );
    },
  },
  {
    id: "error",
    header: "Error",
    justify: "left",
    renderer: ({ value }) => (
      <Text variant="small2" color={value ? Color.red : Color.textSecondary}>
        {value || "-"}
      </Text>
    ),
  },
];

export const ModalImportProductImages = () => {
  const { hasLoader } = useLoaders();
  const { postProductImagesApi } = useApi();

  const [files, setFiles] = useState<File[]>([]);
  const [response, setResponse] = useState<ResponsePostInputsProductImages | null>(null);
  const [uploaderKey, setUploaderKey] = useState(0);

  const isLoading = hasLoader(RoutePostInputsProductImages);

  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles);
    if (response) setResponse(null);
  };

  const uploadImages = async () => {
    if (files.length === 0) return;

    const result = await postProductImagesApi(files);

    if (result) {
      setResponse(result);
      setFiles([]);
      setUploaderKey((k) => k + 1);
    }
  };

  return (
    <Modal name="ModalImportProductImages" title="Import Product Images" width="700px">
      <Flex column padding="10px" gap="16px">
        <MultiFileUploader
          key={uploaderKey}
          fileType="jpg"
          maxFiles={MAX_PRODUCT_IMAGE_UPLOADS}
          isLoading={isLoading}
          onChange={handleFilesChange}
        />

        {response && (
          <Flex column gap="10px">
            <Text variant="body2">
              Total: {response.total} | Succeeded: {response.succeeded} | Failed: {response.failed}
            </Text>

            <SubgridTable columns={columns} data={response.results} />
          </Flex>
        )}

        <Button isDisabled={files.length === 0 || isLoading} onClick={uploadImages}>
          Upload
        </Button>
      </Flex>
    </Modal>
  );
};
